import { ChatPrompt, type Features } from "@storytell/cp-client";
import type { StreamingChatId, StreamingMessage } from "@storytell/streaming";
import { createSignal } from "solid-js";
import { createStore } from "solid-js/store";

export interface ChatTitle {
  chatId: StreamingChatId;
  title: string;
  generatedAt: Date;
}

interface ChatTitleStore {
  titles: Record<StreamingChatId, ChatTitle>;
  loading: Record<StreamingChatId, boolean>;
}

/**
 * Service to manage chat titles
 * Generates descriptive titles from the first user message and AI response
 */
export class ChatTitleService {
  private store: ChatTitleStore;
  private setStore: (state: ChatTitleStore | ((prev: ChatTitleStore) => ChatTitleStore)) => void;

  constructor() {
    const [store, setStore] = createStore<ChatTitleStore>({
      titles: {},
      loading: {},
    });
    this.store = store;
    this.setStore = setStore;
  }

  /**
   * Get title for a specific chat (reactive)
   */
  getChatTitle(chatId: StreamingChatId): string | undefined {
    // Access store reactively
    return this.store.titles[chatId]?.title;
  }

  /**
   * Check if title is being generated
   */
  isGenerating(chatId: StreamingChatId): boolean {
    return this.store.loading[chatId] || false;
  }

  /**
   * Set title for a chat (used when loading from backend)
   */
  setChatTitle(chatId: StreamingChatId, title: string): void {
    const chatTitle: ChatTitle = {
      chatId,
      title,
      generatedAt: new Date(),
    };

    this.setStore((prev) => ({
      ...prev,
      titles: { ...prev.titles, [chatId]: chatTitle },
      loading: { ...prev.loading, [chatId]: false },
    }));
  }

  /**
   * Generate a title for the chat based on the first message
   * This is a placeholder implementation - in production, this would call
   * an LLM endpoint to generate a proper title
   */
  async generateTitle(
    chatId: StreamingChatId,
    firstMessage: StreamingMessage,
    model?: Features.Feature,
  ): Promise<string> {
    // Check if already generating or generated
    if (this.store.loading[chatId]) {
      throw new Error("Title generation already in progress");
    }
    if (this.store.titles[chatId]) {
      return this.store.titles[chatId].title;
    }

    // Set loading state
    this.setStore((prev) => ({
      ...prev,
      loading: { ...prev.loading, [chatId]: true },
    }));

    try {
      // Extract the user's prompt text
      const promptText = firstMessage.prompt?.text || "";

      // Simple title generation logic (placeholder)
      // In production, this would call an API endpoint that uses an LLM
      let title = "New Chat";

      if (promptText) {
        // Create a simple title from the first line or first 50 chars
        const firstLine = promptText.split("\n")[0];
        const truncated = firstLine.substring(0, 50);
        title = truncated.length < firstLine.length ? `${truncated}...` : truncated;

        // Clean up the title
        title = title.trim();
        if (!title) {
          title = "New Chat";
        }
      }

      // Simulate async operation
      await new Promise((resolve) => setTimeout(resolve, 500));

      // Store the generated title
      const chatTitle: ChatTitle = {
        chatId,
        title,
        generatedAt: new Date(),
      };

      this.setStore((prev) => ({
        titles: { ...prev.titles, [chatId]: chatTitle },
        loading: { ...prev.loading, [chatId]: false },
      }));

      return title;
    } catch (error) {
      // Clear loading state on error
      this.setStore((prev) => ({
        ...prev,
        loading: { ...prev.loading, [chatId]: false },
      }));

      throw error instanceof Error ? error : new Error("Failed to generate title");
    }
  }

  /**
   * Manually set a title for a chat
   */
  setTitle(chatId: StreamingChatId, title: string) {
    const chatTitle: ChatTitle = {
      chatId,
      title,
      generatedAt: new Date(),
    };

    this.setStore((prev) => ({
      ...prev,
      titles: { ...prev.titles, [chatId]: chatTitle },
    }));
  }

  /**
   * Clear all stored titles
   */
  clearTitles() {
    this.setStore({
      titles: {},
      loading: {},
    });
  }
}
